/* eslint-disable jsx-a11y/label-has-associated-control */
import { Info, Lightbulb, LoaderCircle, PanelRightClose, PanelRightOpen } from 'lucide-react';
import Markdown from 'markdown-to-jsx';
import { PropsWithChildren, useEffect, useState } from 'react';
import { NewProfile } from '../../ifaces/profile';
import { LlmPrompt } from '../../ifaces/prompt';
import { useLlmQuery } from '../hooks/use-llm-query';
import { Sidebar } from './Sidebar';
import { Text } from './Typography';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

export function ExerciseHelp({
  isOpen,
  onClose,
  hint,
  profile,
  onProfileChange,
  onRegenerate,
  session_id,
  program_slug,
  skill_slug,
  competency_slug,
}: {
  hint?: true | string;
  isOpen: boolean;
  onClose: () => void;
  profile?: NewProfile;
  onProfileChange?: (profile: NewProfile) => void;
  onRegenerate?: (reason: string) => Promise<void>;
  session_id?: string;
  program_slug?: string;
  skill_slug?: string;
  competency_slug?: string;
}) {
  const [accordion, setAccordion] = useState(1); // start with content recall open
  useEffect(() => {
    if (accordion >= 0)
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
  }, [accordion]);
  if (!isOpen) return null;
  return (
    <Sidebar isOpen onClose={onClose} desktopClassName="ml-8">
      <div className="flex flex-col gap-6">
        {hint !== undefined && <HintSection hint={hint} />}
        {session_id && profile && program_slug && skill_slug && competency_slug && (
          <ContentRecallSection
            profile={profile}
            program_slug={program_slug}
            skill_slug={skill_slug}
            competency_slug={competency_slug}
            session_id={session_id}
          />
        )}
        <div>
          <div className="flex gap-1 mb-1">
            <button
              onClick={() => {
                setAccordion((a) => (a === 3 ? -1 : 3));
              }}
              className={`neo sider ${accordion === 3 ? 'on' : ''}`}
            >
              <Text s semiBold style={{ textTransform: 'uppercase' }}>
                <Trans>Edit Customizations</Trans>
              </Text>
            </button>
            <button
              onClick={() => {
                setAccordion((a) => (a === 2 ? -1 : 2));
              }}
              className={`neo sider ${accordion === 2 ? 'on' : ''}`}
            >
              <Text s semiBold style={{ textTransform: 'uppercase' }}>
                <Trans>Report Exercise</Trans>
              </Text>
            </button>
          </div>
          <CustomizationsSection
            profile={profile}
            onProfileChange={onProfileChange}
            onToggle={() => setAccordion((a) => (a === 3 ? -1 : 3))}
            isOpen={accordion === 3}
          />
          {onRegenerate && (
            <RegenSection
              onRegenerate={onRegenerate}
              onToggle={() => setAccordion((a) => (a === 2 ? -1 : 2))}
              isOpen={accordion === 2}
            />
          )}
        </div>
      </div>
    </Sidebar>
  );
}

export function ExerciseHelpButton({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) {
  return (
    <button
      className="neo help flex gap-2"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {isOpen ? (
        <>
          <Trans>HIDE HELP</Trans> <PanelRightOpen size={20} />
        </>
      ) : (
        <>
          <Trans>SHOW HELP</Trans> <PanelRightClose size={20} />
        </>
      )}
    </button>
  );
}

const HintSection = (props: PropsWithChildren<{ hint?: string | true }>) => (
  <div className="p-4 rounded-sm bg-warm-2">
    <div className="flex items-top gap-2 mb-2">
      <div>
        <Lightbulb />
      </div>
      <Text semiBold l warm90>
        {props.hint === true || !props.hint ? (
          <Trans>This exercise has no hints.</Trans>
        ) : (
          props.hint
        )}
      </Text>
    </div>
  </div>
);

function ContentRecallSection(props: {
  program_slug: string;
  skill_slug: string;
  competency_slug: string;
  session_id: string;
  profile: NewProfile;
}) {
  const material = useLlmQuery({
    name: LlmPrompt.CONTENT_RECALL,
    skill: props.skill_slug,
    body: {
      competency: props.competency_slug,
      state: {
        profile: props.profile,
        session: props.session_id,
      },
    },
  });
  const sections = (material.data?.material?.split(/#+/) || []).filter(
    (s: string) => s.trim().length > 0,
  );
  return (
    <>
      {!material.isFetched && (
        <div className="p-4 rounded-sm bg-warm-2 flex gap-2">
          <div className="flex items-top gap-2 mb-2">
            <div>
              <Info size={20} />
            </div>
            <div className="flex items-center gap-2">
              <span>
                <Trans>Generating content...</Trans>
              </span>{' '}
              <LoaderCircle className="animate-spin" />
            </div>
          </div>
        </div>
      )}
      {sections.map((section: string, index: number) => (
        <div key={index} className="p-4 rounded-sm bg-warm-2">
          <div className="flex items-top gap-2 mb-2">
            <div>
              <Info size={20} />
            </div>
            <div>
              <Text regular l warm90>
                <Markdown className="markdown content-recall">{`##${section.trim()}`}</Markdown>
              </Text>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function CustomizationsSection(props: {
  onProfileChange?: (profile: NewProfile) => void;
  profile?: NewProfile;
  isOpen: boolean;
  onToggle(): void;
}) {
  const [newProfile, setNewProfile] = useState<NewProfile | undefined>(props.profile);
  return (
    <>
      {props.isOpen && (
        <div className="p-4 rounded-sm bg-white flex flex-col gap-2">
          <label>
            <Text warm75>
              <Trans>First Name</Trans>
            </Text>
            <input
              type="text"
              className="admin block w-full"
              value={newProfile?.first_name || ''}
              onChange={(e) =>
                setNewProfile((p) => ({ ...(p as NewProfile), first_name: e.target.value }))
              }
            />
          </label>
          <label>
            <Text warm75>
              <Trans>Last Name</Trans>
            </Text>
            <input
              type="text"
              className="admin block w-full"
              value={newProfile?.last_name || ''}
              onChange={(e) =>
                setNewProfile((p) => ({ ...(p as NewProfile), last_name: e.target.value }))
              }
            />
          </label>
          <label>
            <Text warm75>
              <Trans>Industry or company</Trans>
            </Text>
            <textarea
              className="admin block w-full"
              placeholder={t`e.g. Educational startup using AI for personalized tutoring`}
              value={newProfile?.company || ''}
              onChange={(e) =>
                setNewProfile((p) => ({ ...(p as NewProfile), company: e.target.value }))
              }
            />
          </label>
          <label>
            <Text warm75>
              <Trans>Your role or responsibilities</Trans>
            </Text>
            <textarea
              className="admin block w-full"
              placeholder={t`e.g. Senior UX Designer creating AI-driven course flows`}
              value={newProfile?.role || ''}
              onChange={(e) =>
                setNewProfile((p) => ({ ...(p as NewProfile), role: e.target.value }))
              }
            />
          </label>
          <button
            style={{ alignSelf: 'flex-end' }}
            className="neo large"
            onClick={() => {
              props.onProfileChange?.(newProfile as NewProfile);
            }}
          >
            <Trans>Save</Trans>
          </button>
        </div>
      )}
    </>
  );
}

function RegenSection(props: {
  isOpen: boolean;
  onToggle(): void;
  onRegenerate: (reason: string) => Promise<void>;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <>
      {props.isOpen && (
        <div className="p-4 rounded-sm bg-white flex flex-col gap-2">
          <Text xl>
            <Trans>Was there something wrong with this exercise?</Trans>
          </Text>
          <Text l spaced>
            <Trans>Let us know so we can improve it.</Trans>
          </Text>
          <div
            className="flex flex-col gap-3"
            style={{ fontWeight: 700, paddingRight: 20, paddingBottom: 10 }}
          >
            <label className="flex gap-2">
              <input
                onChange={(e) => setReason(e.target.value)}
                type="radio"
                name="feedback"
                value="not_relevant"
              />
              <span>
                <Trans>Wasn&apos;t relevant to my role/job</Trans>
              </span>
            </label>
            <label className="flex gap-2">
              <input
                onChange={(e) => setReason(e.target.value)}
                type="radio"
                name="feedback"
                value="unclear_instructions"
              />
              <span>
                <Trans>Instructions were unclear</Trans>
              </span>
            </label>
            <label className="flex gap-2">
              <input
                onChange={(e) => setReason(e.target.value)}
                type="radio"
                name="feedback"
                value="too_difficult"
              />
              <span>
                <Trans>Too hard or time-consuming</Trans>
              </span>
            </label>
          </div>
          <button
            className="neo large"
            onClick={() => {
              setSubmitted(true);
              props.onRegenerate(reason);
            }}
            style={{ alignSelf: 'flex-end' }}
            disabled={!reason || submitted}
          >
            <Trans>Submit</Trans>
          </button>
        </div>
      )}
    </>
  );
}
